import { forwardRef } from "react";
import type { Fill, Page } from "@deckloom/core/schema";
import { ElementView } from "./ElementView";
import { fillPaint } from "./fills";
import { usePlayer } from "./context";

export interface SlideViewProps {
  page: Page;
  className?: string;
  /** render every element visible with no animation wrappers (used for the outgoing slide of a transition) */
  allVisible?: boolean;
}

function Background({ fill, id }: { fill: Fill | null; id: string }) {
  const { deck, mediaUrl } = usePlayer();
  const fp = fillPaint(fill ?? { kind: "solid", color: { hex: "#ffffff", alpha: 1 } }, `bg-${id}`, mediaUrl, (m) => deck.media[m]);
  return (
    <>
      {fp.defs && <defs>{fp.defs}</defs>}
      <rect x={0} y={0} width={deck.size.w} height={deck.size.h} fill={fp.paint} fillOpacity={fp.opacity} />
    </>
  );
}

/** One slide as an SVG in slide units (pt). Animation state is applied by StepPlayer on `g.anim[data-el]` nodes. */
export const SlideView = forwardRef<SVGSVGElement, SlideViewProps>(function SlideView({ page, className, allVisible = false }, ref) {
  const { deck } = usePlayer();
  const layout = page.layoutId ? deck.layouts[page.layoutId] : undefined;
  const background = page.background ?? layout?.background ?? null;
  return (
    <svg
      ref={ref}
      className={className}
      viewBox={`0 0 ${deck.size.w} ${deck.size.h}`}
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block", width: "100%", height: "100%", userSelect: "none" }}
      data-page={page.id}
    >
      <Background fill={background} id={page.id} />
      {layout && (
        <g data-layer="layout">
          {layout.elements.map((el) => <ElementView key={el.id} el={el} animated={false} />)}
        </g>
      )}
      <g data-layer="content">
        {page.elements.map((el) => <ElementView key={el.id} el={el} animated={!allVisible} />)}
      </g>
    </svg>
  );
});
